import { formatDate, formatDay } from './formatDate';

interface IssueLike {
  fields?: { duedate?: string | null };
  mappedFields?: { intervention_datetime?: string | null };
}

// Scheduled-visit date: Jira's native duedate, or intervention_datetime for the
// desks that use their own field instead (LLF / Flagship).
export const getInterventionDate = (issue?: IssueLike): string | null => {
  const value = issue?.fields?.duedate || issue?.mappedFields?.intervention_datetime;
  return typeof value === 'string' && value ? value : null;
};

// Same date as a local calendar day, for day-granularity comparisons. Sliced, so a
// datetime is the day as written (Paris) rather than projected on the viewer's zone.
export const getInterventionDay = (issue?: IssueLike): Date | null => {
  const value = getInterventionDate(issue);
  if (!value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
};

/** The Europe/Paris day of an instant, as `YYYY-MM-DD` — the business day, whatever the viewer's zone. */
export const parisDayOf = (at: Date): string => at.toLocaleDateString('en-CA', { timeZone: 'Europe/Paris' });

/** Today in Europe/Paris as `YYYY-MM-DD`. */
export const parisToday = (): string => parisDayOf(new Date());

/** « Échéance dépassée » (E18): a deadline strictly before Paris today; no deadline never passes (CA16). */
export const isDeadlinePassed = (deadline: string | null | undefined, today: string = parisToday()): boolean =>
  !!deadline && deadline < today;

const PARIS_TIME: Intl.DateTimeFormatOptions = {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
};

/** Now in Europe/Paris as `HH:mm`, 24h — compared as a string, like the backend does. */
export const parisNowTime = (): string => new Date().toLocaleTimeString('en-GB', PARIS_TIME);

/** A timestamp as the Paris day (locale-formatted) and `HH:mm` — the business clock (CA18). */
export const parisDateTime = (iso: string): { date: string; time: string } => {
  const at = new Date(iso);
  return {
    date: formatDay(parisDayOf(at)),
    time: at.toLocaleTimeString('en-GB', PARIS_TIME),
  };
};

/**
 * `value` as a real `YYYY-MM-DD` calendar day, or null when it is not one. A datetime is sliced to
 * its day, as written, so a Jira field carrying an hour still yields the Paris day.
 * ⚠️ `new Date('2026-02-31')` does not throw — it rolls over to 3 March — so the round trip is
 * what proves the day exists.
 */
export const calendarDay = (value: string | null | undefined): string | null => {
  const day = String(value ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const parsed = new Date(`${day}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === day ? day : null;
};

/** Why a typed day cannot be an intervention date. `empty` carries no sentence of its own. */
export type DayRefusal = 'empty' | 'malformed' | 'past';

/**
 * Whether a `YYYY-MM-DD` day can be saved as an intervention date, and why not when it cannot.
 *
 * Mirrors `interventionDate` in the backend schema: a string comparison against today in Paris,
 * at day granularity, so today is accepted and the two sides never disagree on what today is.
 * ⚠️ `new Date('2026-02-31')` does not throw — it rolls over to 3 March — so the round trip is
 * what proves the day exists. A native date input rarely emits one; the helper does not trust it.
 * @param value - The day as typed, `YYYY-MM-DD`.
 * @param today - Today in Paris, injectable for the tests.
 * @returns The refusal, or `null` when the day is schedulable.
 */
export const interventionDayRefusal = (
  value: string,
  today: string = parisToday()
): DayRefusal | null => {
  if (!value) return 'empty';
  // `!== value` and not merely null: a typed datetime is a day `calendarDay` would happily slice,
  // and this field is a day picker — what it emits must be a day already.
  const day = calendarDay(value);
  if (day === null || day !== value) return 'malformed';
  return day < today ? 'past' : null;
};

/** `day · time`, the day alone, or `null` — the join both the technician's screens read the day with. */
export const dayAndTime = (day: string | null, time: string | null): string | null =>
  day && time ? `${day} · ${time}` : day;

/** `day` + `n` calendar days, as `YYYY-MM-DD` — UTC arithmetic, so no DST hour can shift it. */
export const addDays = (day: string, n: number): string => {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + n);
  return date.toISOString().slice(0, 10);
};

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/** The Monday of `day`'s week. */
export const mondayOf = (day: string): string => addDays(day, -((new Date(`${day}T00:00:00Z`).getUTCDay() + 6) % 7));

/** « 5 oct. », « mer. 7 oct. », « mercredi 7 octobre » — a `YYYY-MM-DD` day in the active locale. */
export const shortDay = (day: string): string => formatDate(`${day}T00:00:00`, { day: 'numeric', month: 'short' });
export const weekdayDay = (day: string): string =>
  formatDate(`${day}T00:00:00`, { weekday: 'short', day: 'numeric', month: 'short' });
export const fullDay = (day: string): string =>
  formatDate(`${day}T00:00:00`, { weekday: 'long', day: 'numeric', month: 'long' });

/** The two ends of a day window, the start without its month when both share it: « 5 » … « 20 oct. ». */
export const windowEnds = (start: string, end: string): { start: string; end: string } => ({
  start: start.slice(0, 7) === end.slice(0, 7) ? formatDate(`${start}T00:00:00`, { day: 'numeric' }) : shortDay(start),
  end: shortDay(end),
});
